import { ReactNode, ReactPortal, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface IPortalProps {
  children: ReactNode;
  hide?: boolean;
  parent?: string;
  wrapperStyle?: React.CSSProperties;
  wrapperClassName?: string;
  delay?: number;
}
const hasDocument = () => typeof document !== "undefined";
const Portal = ({
  children,
  hide,
  parent = "body",
  wrapperStyle = {},
  wrapperClassName,
  delay = 0,
}: IPortalProps): React.ReactPortal | null => {
  const elRef = useRef<HTMLDivElement>(
    hasDocument() ? document.createElement("div") : null
  );

  useEffect(() => {
    if (hasDocument() && elRef.current) {
      let mount: HTMLElement | null = null;
      const retryInterval = 300; // Retry every 100ms
      const maxRetries = 50; // Maximum number of retries
      let retries = 0;
      let timeoutId: NodeJS.Timeout;

      const findParent = () => {
        mount = document.querySelector(parent);
        if (mount && elRef.current) {
          Object.entries(wrapperStyle).forEach(([key, val]) => {
            if (elRef.current) elRef.current.style[key as any] = val;
          });
          if (wrapperClassName) {
            elRef.current.classList.add(wrapperClassName);
          }

          mount.appendChild(elRef.current);
        } else if (retries < maxRetries) {
          retries++;
          timeoutId = setTimeout(findParent, retryInterval);
        }
      };

      timeoutId = setTimeout(findParent, delay);

      return () => {
        if (mount && elRef.current) mount.removeChild(elRef.current);
        clearTimeout(timeoutId);
      };
    }
    // @ts-ignore
    // WARNING: do not add wrapperStyle to the dependency array as it will cause a re-render of the component
  }, [parent]);

  return elRef.current && !hide ? createPortal(children, elRef.current) : null;
};

export default Portal;
